'use client';
import React, { useEffect, useState, useRef } from 'react';
import styles from './scss/clvanalysis.module.scss';
import ApexBarChart from '../../components/custom_components/apexChartsTypes/barChart';
import ApexLineChart from '../../components/custom_components/apexChartsTypes/lineChart';
import DonutChart from '../../components/custom_components/apexChartsTypes/donutChart';
import { clvSummaryNlpLycaData, getLycaCountEachChannelCategory, getLycaDistributionCvlSegement, getLycaDistributionNboSegement, getLycaDistributionRfm, getLycaMothwiseTotalBundleCost, getLycaPercentageContribution, getLycaRevenueCostSims, getLycaTotalRevenuePerCustomer, getLycaTotalRevenueRechargeTyp, getLycaUserCountSim } from '../../apiservice/aiLycaPilot';
import dayjs from 'dayjs';

// 🔹 Typing animation component
const TypingText = ({ text, onDone, speed = 20 }: { text: string; onDone?: () => void; speed?: number }) => {
    const [displayedText, setDisplayedText] = useState('');

    useEffect(() => {
        let i = 0;
        const interval = setInterval(() => {
            setDisplayedText(text.slice(0, i + 1));
            i++;
            if (i === text.length) {
                clearInterval(interval);
                onDone?.();
            }
        }, speed);
        return () => clearInterval(interval);
    }, [text, speed, onDone]);

    return <>{displayedText}</>;
};

interface Section {
    title: string;
    desc: string;
    chart: 'bar' | 'line' | 'donut';
    caption: string;
    footer: string;
    color?: string;
    series: any[];
    categories: string[];
}

const ClvAnalysis = ({ onComplete }: { onComplete?: () => void }) => {
    const [visibleSection, setVisibleSection] = useState(0);
    const [phase, setPhase] = useState<'title' | 'desc' | 'caption' | 'footer' | 'done'>('title');
    const chartRefs = useRef<(HTMLDivElement | null)[]>([]);

    // 🔹 State for API data
    const [seriesbar, setSeriesBar] = useState<any[]>([]);
    const [categories, setCategories] = useState<string[]>([]);

    const [seriesbarWeekDay, setSeriesBarWeekDay] = useState<any[]>([]);
    const [categoriesweekday, setCategoriesweekday] = useState<any[]>([]);
    // 🔹 Fetch API data
    const [categories1, setCategories1] = useState<string[]>([]);
    const [lineChartHourData, setLineChartHourData] = useState<any>([]);
    const [lineChartAmountData, setLineChartAmountData] = useState<any>([]);
    const [categories2, setCategories2] = useState<string[]>([]);
    const [donutSeries, setDonutSeries] = useState<number[]>([0, 0, 0]);
    const [donutLabels, setDonutLabels] = useState<string[]>([]);
    const [seriesBubleWeek, setSeriesBubleWeek] = useState<any[]>([]);
    const [categoriesBubleWeek, setCategoriesBubleWeek] = useState<any[]>([]);
    const [seriesbarBundleData, setSeriesBundleData] = useState<any[]>([]);
    const [categoriesBundleData, setCategoriesBundleData] = useState<any[]>([]);
    const [clvCategories, setClvCategories] = useState<any[]>([]);
    const [lineChartBundleHrs, setLineChartBundleHrs] = useState<any[]>([]);
    const [categoriesNbo, setCategoriesNbo] = useState<any[]>([]);
    const [seriesNbo, setSeriesNbo] = useState<any[]>([]);
    const [categoriesRevenue, setCategoriesRevenue] = useState<string[]>([]);
    const [seriesRevenue, setSeriesRevenue] = useState<any[]>([]);
    const [nlpData, setNlpData] = useState<any>([]);

    useEffect(() => {
        const getLycaUserCountSimData = async () => {
            try {
                const body = { fromYear: '2024', toYear: '2025', domainId: ' ' };
                const res = await getLycaUserCountSim(body);
                if (res?.data?.statusCode === 200) {
                    let x: any[] = res?.data?.response ?? [];
                    if (x.length > 0) {
                        setCategories(x.map((item) => item.SIM_TYPE));
                        setSeriesBar([{ name: 'User Count', data: x.map((item) => item.user_count ?? 0) }]);
                    } else {
                        setSeriesBar([]);
                        setCategories([]);
                    }
                }
            } catch (err) {
                console.error('Error fetching:', err);
            }
        };

        const getLycaRevenueCostSimsData = async () => {
            try {
                const body = { fromYear: '2024', toYear: '2025', domainId: ' ' };
                const res = await getLycaRevenueCostSims(body);

                if (res?.data?.statusCode === 200) {
                    let x: any[] = res?.data?.response ?? [];
                    console.log('xxxxxxxxxxxxxxxxxxx', x);

                    if (x.length > 0) {
                        setCategoriesweekday(x.map((item) => item.SIM_TYPE));
                        setSeriesBarWeekDay([
                            { name: 'User Count', data: x.map((item) => item.user_count ?? 0) },
                        ]);
                    } else {
                        // ✅ Reset correct states
                        setSeriesBarWeekDay([]);
                        setCategoriesweekday([]);
                    }
                }
            } catch (err) {
                console.error('Error fetching:', err);
            }
        };

        const getLycaTotalRevenueRechargeTypData = async () => {
            try {
                const body = { fromYear: '2024', toYear: '2025', domainId: ' ' };
                const res = await getLycaTotalRevenueRechargeTyp(body);

                if (res?.data?.statusCode === 200) {
                    const response: { recharge_name: string; revenue: number }[] =
                        res.data.response;

                    if (response?.length > 0) {
                        // ✅ use recharge_name for x/y axis labels
                        const categories = response.map((item) => item.recharge_name);
                        const data = response.map((item) => item.revenue ?? 0);

                        setLineChartHourData([
                            {
                                name: "Total Revenue",
                                data,
                            },
                        ]);

                        setCategories1(categories);
                    } else {
                        setLineChartHourData([]);
                        setCategories1([]);
                    }
                }
            } catch (error) {
                console.error("Error fetching Total Revenue by Recharge Type:", error);
            }
        };

        const getLycaMothwiseTotalBundleCostData = async () => {
            try {
                const body = { fromYear: "2024", toYear: "2025", domainId: " " };
                const res = await getLycaMothwiseTotalBundleCost(body);

                if (res?.data?.statusCode === 200) {
                    const response: {
                        SIM_TYPE: string;
                        Month: number;
                        Actual_Bundle_Cost: number;
                    }[] = res.data.response;

                    if (response?.length > 0) {
                        // ✅ Extract unique months sorted
                        const months = Array.from(new Set(response.map((item) => item.Month))).sort(
                            (a, b) => a - b
                        );

                        // ✅ Extract unique SIM types
                        const simTypes = Array.from(new Set(response.map((item) => item.SIM_TYPE)));

                        // ✅ Build ApexCharts series (one for each SIM_TYPE)
                        const series = simTypes.map((sim) => ({
                            name: sim,
                            data: months.map((month) => {
                                const record = response.find(
                                    (item) => item.SIM_TYPE === sim && item.Month === month
                                );
                                return record ? record.Actual_Bundle_Cost : 0;
                            }),
                        }));

                        // ✅ Format categories as Jan25, Feb25, ...
                        const year = 2025;
                        const categories = months.map((m) =>
                            dayjs(`${year}-${m}-01`).format("MMMYY")
                        );

                        setCategories2(categories);
                        setLineChartAmountData(series);
                    } else {
                        setCategories2([]);
                        setLineChartAmountData([]);
                    }
                }
            } catch (error) {
                console.error("Error fetching Monthwise Total Bundle Cost:", error);
            }
        };

        const getChargesPercentageData = async () => {
            try {
                const body = { fromYear: "2024", toYear: "2025", domainId: " " };
                const res = await getLycaPercentageContribution(body);

                if (res?.data?.statusCode === 200) {
                    const response = res.data.response; // [{ message_charges, voice_charges, data_charges }]
                    console.log("response---", response);

                    if (response?.length > 0) {
                        const obj = response[0]; // take the first object

                        // Build series and labels
                        const series = [
                            obj.voice_charges ?? 0,
                            obj.data_charges ?? 0,
                            obj.message_charges ?? 0,
                        ];

                        const labels = [
                            "Voice Call Charges",
                            "Data Charges",
                            "Message Charges",
                        ];

                        setDonutSeries(series);
                        setDonutLabels(labels);
                    } else {
                        setDonutSeries([]);
                        setDonutLabels([]);
                    }
                }
            } catch (error) {
                console.error("Error fetching charges percentage:", error);
            }
        };

        const getLycaCountEachChannelCategoryData = async () => {
            try {
                const body = { fromYear: "2024", toYear: "2025", domainId: " " };
                const res = await getLycaCountEachChannelCategory(body);

                if (res?.data?.statusCode === 200) {
                    const response: { CHANNEL_CATEGORY: string; TotalCount: number }[] =
                        res.data.response || [];
                    if (response.length > 0) {
                        // Categories (x-axis)
                        const categories = response.map((item) => item.CHANNEL_CATEGORY);
                        // Series data (y-axis values)
                        const data = response.map((item) => item.TotalCount);
                        // Update chart state
                        setSeriesBubleWeek([
                            {
                                name: "Total Count",
                                data: data,
                            },
                        ]);
                        setCategoriesBubleWeek(categories);
                    } else {
                        setSeriesBubleWeek([{ name: "Total Count", data: [] }]);
                        setCategoriesBubleWeek([]);
                    }
                }
            } catch (error) {
                console.error("Error fetching Channel Category Data:", error);
                setCategoriesBubleWeek([]);
            }
        };

        const getLycaDistributionRfmData = async () => {
            try {
                const body = { fromYear: "2024", toYear: "2025", domainId: " " };
                const res = await getLycaDistributionRfm(body);

                if (res?.data?.statusCode === 200) {
                    let response: { RFM_Category: string; totalCount: number }[] =
                        res.data.response || [];

                    if (response.length > 0) {
                        // 🔹 Move "Others" to the end
                        response = response.sort((a, b) => {
                            if (a.RFM_Category === "Others") return 1;
                            if (b.RFM_Category === "Others") return -1;
                            return 0;
                        });

                        // X-axis categories
                        const categories = response.map(item => item.RFM_Category);

                        // Y-axis values
                        const data = response.map(item => item.totalCount);

                        // Update chart state
                        setSeriesBundleData([
                            {
                                name: "Total Count",
                                data: data,
                            },
                        ]);
                        setCategoriesBundleData(categories);
                    } else {
                        setSeriesBundleData([{ name: "Total Count", data: [] }]);
                        setCategoriesBundleData([]);
                    }
                }
            } catch (error) {
                console.error("Error fetching Distribution RFM Data:", error);
                setSeriesBundleData([{ name: "Total Count", data: [] }]);
                setCategoriesBundleData([]);
            }
        };

        const getLycaDistributionCvlSegementData = async () => {
            try {
                const body = { fromYear: "2024", toYear: "2025", domainId: " " };

                const res = await getLycaDistributionCvlSegement(body);

                if (res?.data?.statusCode !== 200) return;

                const response = res.data.response;

                // X-axis labels = CLV_Segment
                const categories = response.map((item: any) => item.CLV_Segment);

                // Y-axis values = totalCount
                const series = [
                    {
                        name: "Number of Customers",
                        data: response.map((item: any) => item.totalCount),
                    },
                ];

                // Update state
                setClvCategories(categories);
                setLineChartBundleHrs(series);
            } catch (error) {
                console.error("Error fetching CLV Segment distribution:", error);
            }
        };

        const getLycaDistributionNboSegementData = async () => {
            try {
                const body = { fromYear: "2024", toYear: "2025", domainId: " " };
                const res = await getLycaDistributionNboSegement(body);
                console.log("res======", res);

                if (res?.data?.statusCode !== 200) return;

                const response = res.data.response;

                // Categories = NBO_Segment (x-axis labels)
                const categories = response.map((item: any) => item.NBO_Segment);

                // Series = Number of Customers (y-axis values)
                const series = [
                    {
                        name: "Number of Customers",
                        data: response.map((item: any) => item.totalCount),
                    },
                ];

                console.log("categories---", categories);
                console.log("series---", series);

                // update states for chart
                setCategoriesNbo(categories);
                setSeriesNbo(series);
            } catch (error) {
                console.error("Error fetching NBO Segment distribution:", error);
            }
        };

        const getLycaTotalRevenuePerCustomerData = async () => {
            try {
                const body = { fromYear: "2024", toYear: "2025", domainId: " " };
                const res = await getLycaTotalRevenuePerCustomer(body);
                console.log("res======", res);

                if (res?.data?.statusCode !== 200) return;

                const response = res.data.response;

                // x-axis labels = cluster_name
                const categories = response.map((item: any) => item.cluster_name);

                // y-axis values = TotalRevenue
                const series = [
                    {
                        name: "Total Revenue",
                        data: response.map((item: any) => item.TotalRevenue),
                    },
                ];

                console.log("categories---", categories);
                console.log("series---", series);

                setCategoriesRevenue(categories);
                setSeriesRevenue(series);
            } catch (error) {
                console.error("Error fetching Total Revenue per Customer:", error);
            }
        };

        getLycaUserCountSimData();
        getLycaRevenueCostSimsData()
        getLycaTotalRevenueRechargeTypData()
        getLycaMothwiseTotalBundleCostData()
        getChargesPercentageData()
        getLycaCountEachChannelCategoryData()
        getLycaDistributionRfmData()
        getLycaDistributionCvlSegementData()
        getLycaDistributionNboSegementData()
        getLycaTotalRevenuePerCustomerData()
    }, []);

    useEffect(() => {
        const fetchNlpData = async () => {
            try {
                // setLoader(true);

                const res = await clvSummaryNlpLycaData();
                console.log('clvSummaryNlpLycaData=====', res)
                if (res?.status === 200) {
                    setNlpData(res.data.message); // store API result
                    // dispatch(dashboardAction.setClvNlpLycaData(res.data.message))
                }
            } catch (error) {
                console.error("Error fetching NLP data:", error);
            } finally {
                // setLoader(false);
            }
        };
        fetchNlpData();
    }, []);

    // 🔹 Sections (dynamic + static)
    const sections: Section[] = [
        {
            title: 'User Count of each SIM type',
            desc: 'See which SIM type has the larger user base and what drives it',
            chart: 'bar',
            caption: 'x axis SIM Type y axis Count',
            footer: nlpData && nlpData.length > 0 && nlpData[0]?.summary
                ? nlpData[0].summary :
                'Top-up revenue peaked in 2021 and 2024, indicating effective strategies or favorable conditions. In contrast, 2020 and 2023 underperformed, suggesting potential issues to address. Year-over-year trends are inconsistent.',
            color: '#575CFF',
            series: seriesbar, // ✅ live state from API
            categories: categories, // ✅ live state from API
        },
        {
            title: 'Total Revenue Cost of Sim type',
            desc: 'See which SIM type drives the highest revenue and why.',
            chart: 'bar',
            caption: 'x axis SIM Type y axis Total Bundle Cost',
            footer: nlpData && nlpData.length > 1 && nlpData[1]?.summary
                ? nlpData[1].summary : 'Top-up revenue peaked in 2021 and 2024',
            series: seriesbarWeekDay,
            categories: categoriesweekday,
        },
        {
            title: 'Total Revenue per Recharge Type',
            desc: 'Monitor how the total top-up amount fluctuates by hour throughout the years.',
            chart: 'bar',
            caption: 'x axis SIM Type y axis Total Bundle Cost',
            footer: nlpData && nlpData.length > 2 && nlpData[2]?.summary
                ? nlpData[2].summary : 'Offer time-based bonuses or discounts during off-peak hours to encourage more transactions and spread load.',
            series: lineChartHourData,
            categories: categories1
        },
        {
            title: 'Month-wise Total Actual Bundle Cost by SIM Type (Jan-Jun 2025)',
            desc: 'Track which SIM type drove higher bundle costs and how trends shifted.',
            chart: 'line',
            caption: 'x axis Month y axis Total Actual Bundle Cost',
            footer: nlpData && nlpData.length > 3 && nlpData[3]?.summary
                ? nlpData[3].summary : 'Top-up revenue peaked in 2021 and 2024',
            color: '#7423D7',
            series: lineChartAmountData,
            categories: categories2
        },
        {
            title: "Percentage Contribution of charges",
            desc: "Compare service types to uncover the highest cost contributor.",
            chart: "donut",
            caption: "Voice Call Charges",
            footer: nlpData && nlpData.length > 4 && nlpData[4]?.summary
                ? nlpData[4].summary : "Top-up revenue peaked in 2021 and 2024",
            color: "#7423D7",
            series: donutSeries,  // ✅ numbers
            categories: donutLabels   // ✅ labels for donut chart
        },
        {
            title: 'Count of each channel category',
            desc: 'Discover which channels customers prefer the most.',
            chart: 'bar',
            caption: 'x axis Channel Category y axis Count',
            footer: nlpData && nlpData.length > 5 && nlpData[5]?.summary
                ? nlpData[5].summary : 'Top-up revenue peaked in 2021 and 2024, indicating effective strategies or favorable conditions. In contrast, 2020 and 2023 underperformed, suggesting potential issues to address. Year-over-year trends are inconsistent.',
            color: '#575CFF',
            series: seriesBubleWeek,
            categories: categoriesBubleWeek
        },
        {
            title: 'Distribution of RFM Categories',
            desc: 'Discover which RFM segments drive retention and revenue focus.',
            chart: 'bar',
            caption: 'x axis RFM Category   y axis Number of Customers',
            footer: nlpData && nlpData.length > 6 && nlpData[6]?.summary
                ? nlpData[6].summary : 'Top-up revenue peaked in 2021 and 2024, indicating effective strategies or favorable conditions. In contrast, 2020 and 2023 underperformed, suggesting potential issues to address. Year-over-year trends are inconsistent.',
            color: '#575CFF',
            series: seriesbarBundleData,        // ✅ dynamic from API
            categories: categoriesBundleData // ✅ dynamic from API
        },
        {
            title: 'Distribution of CLV Segments',
            desc: 'Track customer distribution by lifetime value segments.',
            chart: 'bar',
            caption: 'x axis CLV Segment   y axis Number of Customers',
            footer: nlpData && nlpData.length > 7 && nlpData[7]?.summary
                ? nlpData[7].summary : 'Top-up revenue peaked in 2021 and 2024, indicating effective strategies or favorable conditions. In contrast, 2020 and 2023 underperformed, suggesting potential issues to address. Year-over-year trends are inconsistent.',
            color: '#575CFF',
            series: lineChartBundleHrs,
            categories: clvCategories
        },
        {
            title: 'Distribution of NBO Segments',
            desc: 'Compare segments to balance retention and revenue growth.',
            chart: 'bar',
            caption: 'x axis CLV Segment   y axis Number of Customers',
            footer: nlpData && nlpData.length > 8 && nlpData[8]?.summary
                ? nlpData[8].summary : 'Top-up revenue peaked in 2021 and 2024, indicating effective strategies or favorable conditions. In contrast, 2020 and 2023 underperformed, suggesting potential issues to address. Year-over-year trends are inconsistent.',
            color: '#575CFF',
            series: seriesNbo,
            categories: categoriesNbo
        },
        {
            title: 'Total Revenue per Customer Segment',
            desc: 'Compare customer segments to identify where revenue is strongest.',
            chart: 'bar',
            caption: 'x axis Total Revenue   y axis Customer Segment',
            footer: nlpData && nlpData.length > 9 && nlpData[9]?.summary
                ? nlpData[9].summary : 'Top-up revenue peaked in 2021 and 2024, indicating effective strategies or favorable conditions. In contrast, 2020 and 2023 underperformed, suggesting potential issues to address. Year-over-year trends are inconsistent.',
            color: '#575CFF',
            series: seriesRevenue,
            categories: categoriesRevenue
        },
    ];

    // 🔹 Scroll when section changes
    useEffect(() => {
        const currentRef = chartRefs.current[visibleSection];
        if (currentRef) {
            currentRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [visibleSection, phase]);

    // Reset to title when moving to new section
    useEffect(() => {
        setPhase('title');
    }, [visibleSection]);

    return (
        <div className={styles.clvchartssec}>
            {sections.map((current, i) => {
                const isActive = i === visibleSection;
                const isCompleted = i < visibleSection;

                if (!isActive && !isCompleted) return null;

                return (
                    <div key={i} ref={(el) => (chartRefs.current[i] = el)} className={styles.clvchartwrapper}>
                        {/* HEADER */}
                        <div className={styles.clvwrapheader}>
                            <p className={styles.clvwrapheadertxt}>
                                {isActive && phase === 'title' ? (
                                    <TypingText text={current.title} onDone={() => setPhase('desc')} />
                                ) : (
                                    current.title
                                )}
                            </p>
                            <span className={styles.clvwrpadesc}>
                                {isActive && phase === 'desc' ? (
                                    <TypingText text={current.desc} onDone={() => setPhase('caption')} />
                                ) : isCompleted || (isActive && ['caption', 'footer', 'done'].includes(phase)) ? (
                                    current.desc
                                ) : null}
                            </span>
                        </div>

                        {/* BODY */}
                        {(isCompleted || (isActive && ['caption', 'footer', 'done'].includes(phase))) && (
                            <div className={styles.clvwrapbody}>
                                <p className={styles.clvwrapcaptions}>
                                    {isActive && phase === 'caption' ? (
                                        <TypingText text={current.caption} onDone={() => setPhase('footer')} />
                                    ) : (
                                        current.caption
                                    )}
                                </p>
                                {current.chart === 'bar' ? (
                                    <ApexBarChart
                                        isStacked={false}
                                        series={current.series}
                                        colors={[current.color || '#575CFF']}
                                        categories={current.categories}
                                    />
                                ) : current.chart === 'donut' ? (
                                    <DonutChart
                                        series={current.series}
                                        labels={current.categories}
                                    />) : (
                                    <ApexLineChart series={current.series} categories={current.categories} />
                                )}
                            </div>
                        )}

                        {/* FOOTER */}
                        {(isCompleted || (isActive && ['footer', 'done'].includes(phase))) && (
                            <div className={styles.clvwrapfooter}>
                                <button className="insightsbtn">AI Insights</button>
                                <p className={styles.clvwrapfttxt}>
                                    {isActive && phase === 'footer' ? (
                                        <TypingText
                                            text={current.footer}
                                            onDone={() => {
                                                setPhase('done');
                                                setTimeout(() => {
                                                    if (visibleSection < sections.length - 1) {
                                                        setVisibleSection((prev) => prev + 1);
                                                    } else {
                                                        onComplete?.();
                                                    }
                                                }, 500);
                                            }}
                                        />
                                    ) : (
                                        current.footer
                                    )}
                                </p>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default ClvAnalysis;